import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Drawer from "@mui/material/Drawer";
import SelectField from "../../../../Components/Masters/SelectField";
import countries from "../../Roles and Permissions/Country.json";
import EditDialog from "../../../../Components/Common/EditDialog";
import { post, put } from "../../../../utils/api";
import {
  INVESTMENT_TYPE_OPTION_GROUPS,
  INVESTMENT_TYPE_OPTIONS,
} from "../../../../utils/investmentTypeGroups";

const SELECT_MENU_ITEM_SX = {
  width: "100%",
  overflow: "hidden",
  whiteSpace: "normal",
  textOverflow: "clip",
  maxWidth: "none",
  wordBreak: "break-word",
  boxSizing: "border-box",
};

const INVESTMENT_TYPE_OPTION_SX = {
  ...SELECT_MENU_ITEM_SX,
  pl: 4,
  position: "relative",
  alignItems: "flex-start",
  "&::before": {
    content: '"\\2022"',
    position: "absolute",
    left: "1.25rem",
    top: "0.45rem",
    fontSize: "1.35rem",
    lineHeight: 1,
  },
};

const FIELD_ERROR_SX = {
  fontFamily:
    'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  fontSize: "0.875rem",
  fontWeight: 400,
  lineHeight: 1.43,
  letterSpacing: "0.01786em",
  mt: "3px",
};

const renderFieldError = (message) =>
  message ? (
    <Typography variant="body2" color="#D34040" sx={FIELD_ERROR_SX}>
      {message}
    </Typography>
  ) : null;

const SECTOR_OPTIONS = [
  "Energy (renewable & non-renewable)",
  "Manufacturing & industrials",
  "Transportation & logistics",
  "Real estate & infrastructure",
  "Agriculture, forestry & land use",
  "Mining & metals",
  "Oil & gas",
  "Utilities (power, water, waste)",
  "Technology & data centers",
  "Retail & consumer goods",
  "Healthcare & pharmaceuticals",
  "Financial services & fintech",
  "Telecommunications",
  "Construction & materials",
];

const CONSOLIDATION_APPROACH_OPTIONS = [
  "Equity share",
  "Financial control",
  "Operational control",
];

const SCOPE12_DEPENDENT_FIELDS = new Set([
  "consolidationApproach",
  "portfolioBasisStartYear",
  "portfolioBasisEndYear",
  "thresholdValue",
  "includeInScope3",
]);

const isScope12DependentFieldVisible = (formInputData, formData, fieldName) => {
  if (!SCOPE12_DEPENDENT_FIELDS.has(fieldName)) return true;
  const hasAlreadyIncluded = formInputData.some(
    (input) => input.fieldName === "alreadyIncluded"
  );
  if (!hasAlreadyIncluded) return true;
  return formData.alreadyIncluded !== "yes";
};

const shouldSkipScope12DependentFields = (formInputData, formData) =>
  formInputData.some((input) => input.fieldName === "alreadyIncluded") &&
  formData.alreadyIncluded === "yes";

const buildPortfolioStartYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const lastYear = currentYear - 1;
  const startYear = 2013;
  const totalYears = Math.max(0, lastYear - startYear + 1);
  return Array.from({ length: totalYears }, (_, i) => startYear + i);
};

const buildPortfolioEndYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const startYear = 2013;
  const totalYears = Math.max(0, currentYear - startYear + 1);
  return Array.from({ length: totalYears }, (_, i) => startYear + i);
};

const isPortfolioEndYearValid = (startYear, endYear) => {
  if (Number.isNaN(startYear) || Number.isNaN(endYear)) return true;
  return endYear > startYear;
};

const toDateInputValue = (value) => {
  if (!value) return "";
  if (typeof value === "string") {
    const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().split("T")[0];
};

const inferPortfolioStartYear = (iso) => {
  const full = toDateInputValue(iso);
  if (!full) return "";
  const [ys, ms, ds] = full.split("-");
  const y = parseInt(ys, 10);
  const m = parseInt(ms, 10);
  const d = parseInt(ds, 10);
  if (m === 4 && d === 1) return ys;
  if (m > 4 || (m === 4 && d > 1)) return String(y);
  if (m < 4) return String(y - 1);
  return String(y);
};

const inferPortfolioEndYear = (iso) => {
  const full = toDateInputValue(iso);
  if (!full) return "";
  const [ys, ms, ds] = full.split("-");
  const y = parseInt(ys, 10);
  const m = parseInt(ms, 10);
  const d = parseInt(ds, 10);
  if (m === 3 && d === 31) return ys;
  if (m < 3 || (m === 3 && d < 31)) return String(y);
  return String(y + 1);
};

const buildPayloadFromForm = (formInputData, formData) => {
  const fieldNames = new Set(formInputData.map((input) => input.fieldName));
  const skipScope12Fields = shouldSkipScope12DependentFields(formInputData, formData);
  const payload = {};

  if (fieldNames.has("investmentName")) {
    payload.investmentName = formData.investmentName.trim();
  }
  if (fieldNames.has("alreadyIncluded")) {
    payload.alreadyIncluded = formData.alreadyIncluded === "yes";
  }
  if (fieldNames.has("consolidationApproach")) {
    payload.consolidationApproach = skipScope12Fields
      ? null
      : formData.consolidationApproach;
  }
  if (fieldNames.has("portfolioBasisStartYear")) {
    payload.portfolioBasisDateStart =
      skipScope12Fields || !formData.portfolioBasisStartYear
        ? null
        : `${formData.portfolioBasisStartYear}-04-01`;
  }
  if (fieldNames.has("portfolioBasisEndYear")) {
    payload.portfolioBasisDateEnd =
      skipScope12Fields || !formData.portfolioBasisEndYear
        ? null
        : `${formData.portfolioBasisEndYear}-03-31`;
  }
  if (fieldNames.has("thresholdValue")) {
    payload.thresholdValue = skipScope12Fields
      ? null
      : Number(formData.thresholdValue);
  }
  if (fieldNames.has("includeInScope3")) {
    payload.includeInScope3 = skipScope12Fields
      ? false
      : formData.includeInScope3 === "yes";
  }
  if (fieldNames.has("investmentType")) {
    payload.investmentType = formData.investmentType;
  }
  if (fieldNames.has("sector")) {
    payload.sector = formData.sector;
  }
  if (fieldNames.has("countryOfOperation")) {
    payload.countryOfOperation = formData.countryOfOperation;
  }
  if (fieldNames.has("equityHoldingPercent")) {
    payload.equityHoldingPercent = Number(formData.equityHoldingPercent);
  }

  return payload;
};

function AddInvestment({
  formInputData,
  fetchData,
  id,
  element,
  setAddData,
  addData,
  setsnackOpen,
  addUrl,
  updateUrl,
  embedded = false,
  embeddedTitle = "",
}) {
  const [errors, setErrors] = useState({});
  const investmentTypeFieldRef = useRef(null);
  const [investmentTypeMenuWidth, setInvestmentTypeMenuWidth] = useState(null);

  const buildInitialState = () => ({
    investmentName: element?.investmentName || "",
    alreadyIncluded: element?.alreadyIncluded ? "yes" : "no",
    consolidationApproach: element?.consolidationApproach || "",
    portfolioBasisStartYear: inferPortfolioStartYear(
      element?.portfolioBasisDateStart
    ),
    portfolioBasisEndYear: inferPortfolioEndYear(element?.portfolioBasisDateEnd),
    thresholdValue:
      element?.thresholdValue !== undefined && element?.thresholdValue !== null
        ? String(element.thresholdValue)
        : "",
    includeInScope3: element?.includeInScope3 === false ? "no" : "yes",
    investmentType: element?.investmentType || "",
    sector: element?.sector || "",
    countryOfOperation: element?.countryOfOperation || "",
    equityHoldingPercent:
      element?.equityHoldingPercent !== undefined
        ? String(element.equityHoldingPercent)
        : "",
  });

  const [formData, setFormData] = useState(buildInitialState);
  const [editDialog, setEditDialog] = useState(false);
  const [formData2, setFormData2] = useState(buildInitialState);

  useEffect(() => {
    const next = buildInitialState();
    setFormData(next);
    setFormData2(next);
  }, [element, id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "equityHoldingPercent" && value !== "" && !/^\d*\.?\d*$/.test(value)) {
      return;
    }
    if (name === "thresholdValue" && value !== "") {
      if (!/^\d*\.?\d{0,2}$/.test(value)) {
        return;
      }
      const thresholdNum = Number(value);
      if (value !== "." && !Number.isNaN(thresholdNum) && thresholdNum > 100) {
        return;
      }
    }

    setFormData((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === "alreadyIncluded" && value === "yes") {
        updated.consolidationApproach = "";
        updated.portfolioBasisStartYear = "";
        updated.portfolioBasisEndYear = "";
        updated.thresholdValue = "";
        updated.includeInScope3 = "no";
      }
      if (name === "portfolioBasisStartYear") {
        const startYear = parseInt(value, 10);
        const endYear = parseInt(prev.portfolioBasisEndYear, 10);
        if (!isPortfolioEndYearValid(startYear, endYear)) {
          updated.portfolioBasisEndYear = "";
        }
      }
      return updated;
    });
    if (name === "alreadyIncluded" && value === "yes") {
      setErrors((prev) => {
        const next = { ...prev, [name]: "" };
        SCOPE12_DEPENDENT_FIELDS.forEach((field) => {
          delete next[field];
        });
        return next;
      });
    } else {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleCountryChange = (event) => {
    const selectedCountry = event.target.value;
    setErrors((prev) => ({ ...prev, countryOfOperation: "" }));
    setFormData((prev) => ({
      ...prev,
      countryOfOperation:
        selectedCountry === "Tap to select" ? "" : selectedCountry,
    }));
  };

  const handleExitAgree = () => {
    setEditDialog(false);
    setAddData(false);
    setErrors({});
    const cleared = buildInitialState();
    setFormData(cleared);
    setFormData2(cleared);
  };

  const handleClose = () => {
    const unchanged = Object.keys(formData).every(
      (key) => formData[key] === formData2[key]
    );
    if (unchanged) handleExitAgree();
    else setEditDialog(true);
  };

  const validateForm = () => {
    const next = { ...errors };
    const fieldNames = new Set(formInputData.map((input) => input.fieldName));

    formInputData.forEach((input) => {
      if (!isScope12DependentFieldVisible(formInputData, formData, input.fieldName)) {
        delete next[input.fieldName];
        return;
      }
      if (input.type === "boolean") {
        return;
      }
      if (input.type === "portfolioYear") {
        if (!input.optional && !formData[input.fieldName]) {
          next[input.fieldName] = `${input.title} is required`;
        } else {
          delete next[input.fieldName];
        }
        return;
      }
      const value = formData[input.fieldName];
      if (!value || String(value).trim() === "") {
        next[input.fieldName] = `${input.title} is required`;
      } else {
        delete next[input.fieldName];
      }
    });

    if (
      fieldNames.has("portfolioBasisStartYear") &&
      fieldNames.has("portfolioBasisEndYear") &&
      isScope12DependentFieldVisible(formInputData, formData, "portfolioBasisStartYear")
    ) {
      if (formData.portfolioBasisStartYear && formData.portfolioBasisEndYear) {
        const startYear = parseInt(formData.portfolioBasisStartYear, 10);
        const endYear = parseInt(formData.portfolioBasisEndYear, 10);
        if (!isPortfolioEndYearValid(startYear, endYear)) {
          next.portfolioBasisEndYear =
            "Portfolio basis end year must be greater than the start year.";
        }
      }
    }

    if (
      fieldNames.has("thresholdValue") &&
      isScope12DependentFieldVisible(formInputData, formData, "thresholdValue")
    ) {
      const threshold = Number(formData.thresholdValue);
      if (Number.isNaN(threshold) || threshold <= 0 || threshold > 100) {
        next.thresholdValue =
          "Threshold for excluding investments (%) must be between 0 and 100";
      }
    }

    if (fieldNames.has("equityHoldingPercent") && formData.equityHoldingPercent !== "") {
      const eq = Number(formData.equityHoldingPercent);
      if (Number.isNaN(eq) || eq < 0 || eq > 100) {
        next.equityHoldingPercent =
          "Allocation Share (%) must be between 0 and 100";
      }
    }

    setErrors(next);
    return !Object.keys(next).some((k) => next[k]);
  };

  const Save = async () => {
    if (!validateForm()) return;

    const payload = buildPayloadFromForm(formInputData, formData);

    const request = id
      ? put(`${updateUrl}?Id=${id}`, payload)
      : post(addUrl, payload);

    await request
      .then((res) => {
        setsnackOpen({
          open: true,
          message: res.data.message,
          severity: "success",
        });
        fetchData();
        handleExitAgree();
      })
      .catch((err) => {
        const apiMessage =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "";
        const isDuplicateNameError =
          typeof apiMessage === "string" &&
          /already exists|already used|duplicate/i.test(apiMessage);

        if (isDuplicateNameError) {
          setErrors((prev) => ({
            ...prev,
            investmentName: "Name already used",
          }));
          return;
        }

        if (err.response?.data?.errors) {
          const errorMessages = err.response.data.errors
            .map((error) => error.msg)
            .join(", ");
          setsnackOpen({
            open: true,
            message: errorMessages,
            severity: "error",
          });
        } else {
          setsnackOpen({
            open: true,
            message: err?.response?.data?.message || "Something went wrong",
            severity: "error",
          });
        }
      });
  };

  const renderPortfolioYearField = (el) => (
    <Box key={el.fieldName}>
      <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: 1 }}>
        {el.optional ? `${el.title} (optional)` : el.title}
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1.5,
          flexWrap: "nowrap",
        }}
      >
        <TextField
          size="small"
          value={el.fixedDateText}
          disabled
          sx={{
            width: 130,
            "& .MuiInputBase-input.Mui-disabled": {
              WebkitTextFillColor: "#6b7280",
              color: "#6b7280",
            },
            "& .MuiInputBase-root.Mui-disabled": {
              backgroundColor: "#f3f4f6",
            },
          }}
        />
        <TextField
          select
          size="small"
          name={el.fieldName}
          sx={{ width: 130 }}
          value={formData[el.fieldName] || ""}
          onChange={handleChange}
          error={Boolean(errors[el.fieldName])}
          SelectProps={{ displayEmpty: true }}
        >
          <MenuItem value="">Select year</MenuItem>
          {(el.fieldName === "portfolioBasisStartYear"
            ? buildPortfolioStartYearOptions()
            : buildPortfolioEndYearOptions()
          )
            .filter((yr) => {
              if (el.fieldName !== "portfolioBasisEndYear") return true;
              const startYear = parseInt(formData.portfolioBasisStartYear, 10);
              if (Number.isNaN(startYear)) return true;
              return yr > startYear;
            })
            .map((yr) => (
              <MenuItem key={yr} value={String(yr)}>
                {yr}
              </MenuItem>
            ))}
        </TextField>
      </Box>
      {renderFieldError(errors[el.fieldName])}
    </Box>
  );

  const renderInvestmentTypeField = (el) => {
    const currentValue = formData.investmentType;
    const hasLegacyValue =
      currentValue && !INVESTMENT_TYPE_OPTIONS.includes(currentValue);

    return (
      <Box key={el.fieldName} ref={investmentTypeFieldRef}>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: 1 }}>
          {el.title}
        </Typography>
        <TextField
          select
          fullWidth
          size="small"
          name={el.fieldName}
          value={currentValue}
          onChange={handleChange}
          error={Boolean(errors[el.fieldName])}
          SelectProps={{
            displayEmpty: true,
            renderValue: (selected) => selected || "Tap to Select",
            onOpen: () => {
              if (investmentTypeFieldRef.current) {
                setInvestmentTypeMenuWidth(
                  investmentTypeFieldRef.current.offsetWidth
                );
              }
            },
            MenuProps: {
              PaperProps: {
                style: {
                  maxHeight: "55%",
                  overflowY: "auto",
                  ...(investmentTypeMenuWidth
                    ? { width: investmentTypeMenuWidth }
                    : {}),
                },
              },
              anchorOrigin: { vertical: "bottom", horizontal: "left" },
              transformOrigin: { vertical: "top", horizontal: "left" },
            },
          }}
        >
          <MenuItem value="" disabled sx={SELECT_MENU_ITEM_SX}>
            Tap to Select
          </MenuItem>
          {hasLegacyValue ? (
            <MenuItem value={currentValue} sx={SELECT_MENU_ITEM_SX}>
              {currentValue}
            </MenuItem>
          ) : null}
          {INVESTMENT_TYPE_OPTION_GROUPS.flatMap((group) => [
            <MenuItem
              key={`${group.label}-header`}
              disabled
              dense
              sx={{
                ...SELECT_MENU_ITEM_SX,
                fontWeight: 700,
                fontSize: "1rem",
                color: "text.primary",
                opacity: "1 !important",
                cursor: "default",
              }}
            >
              {group.label}
            </MenuItem>,
            ...group.options.map((option) => (
              <MenuItem key={option} value={option} sx={INVESTMENT_TYPE_OPTION_SX}>
                {option}
              </MenuItem>
            )),
          ])}
        </TextField>
        {renderFieldError(errors[el.fieldName])}
      </Box>
    );
  };

  const renderField = (el) => {
    if (!isScope12DependentFieldVisible(formInputData, formData, el.fieldName)) {
      return null;
    }

    if (el.type === "portfolioYear") {
      return renderPortfolioYearField(el);
    }

    if (el.type === "boolean") {
      return (
        <Box key={el.fieldName}>
          <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: 1 }}>
            {el.title}
          </Typography>
          <RadioGroup
            row
            name={el.fieldName}
            value={formData[el.fieldName]}
            onChange={handleChange}
          >
            <FormControlLabel value="yes" control={<Radio />} label="Yes" />
            <FormControlLabel value="no" control={<Radio />} label="No" />
          </RadioGroup>
        </Box>
      );
    }

    if (el.fieldName === "consolidationApproach") {
      return (
        <SelectField
          key={el.fieldName}
          title={el.title}
          name={el.fieldName}
          value={formData.consolidationApproach}
          handleChange={handleChange}
          options={CONSOLIDATION_APPROACH_OPTIONS}
          errors={errors[el.fieldName]}
        />
      );
    }

    if (el.fieldName === "investmentType") {
      return renderInvestmentTypeField(el);
    }

    if (el.fieldName === "sector") {
      return (
        <SelectField
          key={el.fieldName}
          title={el.title}
          name={el.fieldName}
          value={formData.sector}
          handleChange={handleChange}
          options={SECTOR_OPTIONS}
          errors={errors[el.fieldName]}
        />
      );
    }

    if (el.fieldName === "countryOfOperation") {
      return (
        <SelectField
          key={el.fieldName}
          title={el.title}
          name={el.fieldName}
          value={formData.countryOfOperation}
          handleChange={handleCountryChange}
          options={countries.countries}
          errors={errors[el.fieldName]}
        />
      );
    }

    return (
      <Box key={el.fieldName}>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: 1 }}>
          {el.title}
        </Typography>
        <TextField
          size="small"
          fullWidth
          variant="outlined"
          name={el.fieldName}
          value={formData[el.fieldName] || ""}
          onChange={handleChange}
          placeholder={el.placeholder}
          disabled={Boolean(el.readOnly)}
          error={Boolean(errors[el.fieldName])}
          inputProps={
            el.fieldName === "equityHoldingPercent" ||
            el.fieldName === "thresholdValue"
              ? { inputMode: "decimal" }
              : undefined
          }
        />
        {renderFieldError(errors[el.fieldName])}
      </Box>
    );
  };

  const drawerList = () => (
    <Box display="flex" gap={3} sx={{ width: "28rem", maxWidth: "100%" }}>
      {editDialog && (
        <EditDialog {...{ editDialog, setEditDialog, handleExitAgree }} />
      )}
      <Box
        sx={{
          backgroundColor: "#fff",
          height: "20px",
          padding: "13px",
          margin: "30px 0 0 0",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          borderRadius: "80%",
          width: "5%",
        }}
      >
        <IconButton onClick={handleClose} sx={{ color: "black" }}>
          <CloseIcon sx={{ fontSize: "1.2rem" }} />
        </IconButton>
      </Box>
      <Box
        sx={{
          width: "90%",
          p: 3,
          backgroundColor: "#fff",
          height: "100vh",
          overflowY: "scroll",
        }}
      >
        <Typography
          variant="h6"
          sx={{ textDecoration: "underline", fontWeight: "bold", mb: 2 }}
        >
          {id ? "Edit Investment" : "Add Investment"}
        </Typography>
        {formInputData.map((el) => renderField(el))}
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#0a3f73",
              textTransform: "none",
              marginTop: "10%",
            }}
            onClick={Save}
          >
            Submit
          </Button>
        </Box>
      </Box>
    </Box>
  );

  const embeddedList = () => (
    <Box
      sx={{
        width: "100%",
        p: 0,
        backgroundColor: "transparent",
      }}
    >
      {editDialog && (
        <EditDialog {...{ editDialog, setEditDialog, handleExitAgree }} />
      )}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography
          variant="h6"
          sx={{ textDecoration: "underline", fontWeight: "bold" }}
        >
          {embeddedTitle || (id ? "Edit Investment" : "Add Investment")}
        </Typography>
      </Box>
      {formInputData.map((el) => renderField(el))}
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          sx={{
            backgroundColor: "#0a3f73",
            textTransform: "none",
            mt: 1,
            mb: 6,
          }}
          onClick={Save}
        >
          Submit
        </Button>
      </Box>
    </Box>
  );

  if (embedded) {
    return addData ? embeddedList() : null;
  }

  return (
    <Drawer
      sx={{
        backgroundColor: "transparent",
        height: "100vh",
        minHeight: "100vh",
        overflowY: "auto",
        "& .MuiDrawer-paper": {
          backgroundColor: "transparent",
          boxShadow: "none",
          height: "100vh",
          minHeight: "100vh",
          overflowY: "auto",
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
      anchor="right"
      open={addData}
      onClose={handleClose}
    >
      {drawerList()}
    </Drawer>
  );
}

export default AddInvestment;
